import { Suspense, useMemo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment, useMutation } from 'react-relay';
import Fuse from 'fuse.js';
import { QueryErrorBoundary } from '@/components/QueryErrorBoundary';
import type { DiscoverPageQuery } from '@/__generated__/DiscoverPageQuery.graphql';
import type { DiscoverPage_topics$key } from '@/__generated__/DiscoverPage_topics.graphql';
import type { DiscoverPageFollowMutation } from '@/__generated__/DiscoverPageFollowMutation.graphql';
import type { DiscoverPageUnfollowMutation } from '@/__generated__/DiscoverPageUnfollowMutation.graphql';

/**
 * Root query for DiscoverPage - fetches all active topics
 */
const discoverPageQuery = graphql`
  query DiscoverPageQuery {
    ...DiscoverPage_topics
  }
`;

/**
 * Fragment for paginated topics
 */
const DiscoverPage_topics = graphql`
  fragment DiscoverPage_topics on QueryRoot
  @refetchable(queryName: "DiscoverPageRefetchQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 50 }
    after: { type: "String" }
  ) {
    topics(first: $first, after: $after)
      @connection(key: "DiscoverPage_topics") {
      edges {
        node {
          id
          slug
          name
          isActive
          s3AnchorImagePath
          isFollowedByViewer
          followerCount
          episodeCount
          episodes(first: 1) {
            edges {
              node {
                publishedAt
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/**
 * Mutation to follow a topic
 */
const followTopicMutation = graphql`
  mutation DiscoverPageFollowMutation($input: FollowTopicInput!) {
    followTopic(input: $input) {
      topic {
        id
        isFollowedByViewer
        followerCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

/**
 * Mutation to unfollow a topic
 */
const unfollowTopicMutation = graphql`
  mutation DiscoverPageUnfollowMutation($input: UnfollowTopicInput!) {
    unfollowTopic(input: $input) {
      topic {
        id
        isFollowedByViewer
        followerCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

interface TopicData {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly isActive: boolean;
  readonly s3AnchorImagePath: string | null | undefined;
  readonly isFollowedByViewer: boolean | null | undefined;
  readonly followerCount: number;
  readonly episodeCount: number | null | undefined;
  readonly episodes: {
    readonly edges: ReadonlyArray<{
      readonly node: {
        readonly publishedAt: string;
      };
    }>;
  };
}

interface TopicCardProps {
  topic: TopicData;
  onFollow: (topicSlug: string) => void;
  onUnfollow: (topicSlug: string) => void;
  isPending: boolean;
}

function TopicCard({ topic, onFollow, onUnfollow, isPending }: TopicCardProps) {
  const cdnHost = import.meta.env.VITE_CDN_HOST;
  const imageUrl = topic.s3AnchorImagePath
    ? `${cdnHost}/${topic.s3AnchorImagePath}`
    : null;

  const latestEpisodeDate = topic.episodes.edges[0]?.node.publishedAt;
  const isFollowing = topic.isFollowedByViewer === true;

  return (
    <div className="group relative rounded-xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 overflow-hidden transition-all hover:border-gray-700">
      {/* Cover image or placeholder */}
      <div className="aspect-square w-full bg-gray-800">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={topic.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-900/50 to-purple-900/50">
            <svg
              className="h-12 w-12 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Topic info */}
      <div className="p-4">
        <Link
          to={`/p/${topic.slug}`}
          className="block font-semibold text-white hover:text-indigo-400 transition-colors line-clamp-1"
        >
          {topic.name}
        </Link>

        <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
          <span>{topic.episodeCount ?? 0} episode{topic.episodeCount !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span>{topic.followerCount} follower{topic.followerCount !== 1 ? 's' : ''}</span>
        </div>

        {latestEpisodeDate && (
          <div className="mt-1 text-xs text-gray-500">
            Updated {new Date(latestEpisodeDate).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
          </div>
        )}

        {/* Follow/Unfollow button */}
        <button
          type="button"
          onClick={() => isFollowing ? onUnfollow(topic.slug) : onFollow(topic.slug)}
          disabled={isPending}
          className={`mt-3 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isFollowing
              ? 'border border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
              : 'bg-indigo-600 text-white hover:bg-indigo-500'
          }`}
        >
          {isPending ? (
            <>
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {isFollowing ? 'Unfollowing...' : 'Following...'}
            </>
          ) : isFollowing ? (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
              Following
            </>
          ) : (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Follow
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ searchQuery }: { searchQuery: string }) {
  return (
    <div className="text-center py-16">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
        <svg
          className="h-8 w-8 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">
        {searchQuery ? 'No matching topics' : 'No topics available'}
      </h3>
      <p className="text-gray-400 max-w-md mx-auto">
        {searchQuery
          ? 'Try adjusting your search terms or browse all topics.'
          : 'Check back soon for new podcasts to discover!'}
      </p>
    </div>
  );
}

interface DiscoverPageContentProps {
  queryRef: DiscoverPage_topics$key;
}

function DiscoverPageContent({ queryRef }: DiscoverPageContentProps) {
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    DiscoverPage_topics,
    queryRef
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [commitFollow, isFollowingMutation] = useMutation<DiscoverPageFollowMutation>(followTopicMutation);
  const [commitUnfollow, isUnfollowingMutation] = useMutation<DiscoverPageUnfollowMutation>(unfollowTopicMutation);

  // Filter only active topics
  const allTopics = useMemo(() => {
    return data.topics.edges
      .map((edge) => edge.node)
      .filter((topic) => topic.isActive);
  }, [data.topics.edges]);

  // Fuse.js for search
  const fuse = useMemo(
    () => new Fuse(allTopics, {
      keys: ['name', 'slug'],
      threshold: 0.4,
    }),
    [allTopics]
  );

  // Filtered results
  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) {
      return allTopics;
    }
    return fuse.search(searchQuery).map((r) => r.item);
  }, [searchQuery, fuse, allTopics]);

  const handleFollow = useCallback((topicSlug: string) => {
    const topicId = allTopics.find((t) => t.slug === topicSlug)?.id;
    if (!topicId) return;
    commitFollow({
      variables: {
        input: { topicSlug },
      },
      optimisticUpdater: (store) => {
        const topicRecord = store.get(topicId);
        if (topicRecord) {
          const currentFollowerCount = topicRecord.getValue('followerCount') as number | null;
          topicRecord.setValue(true, 'isFollowedByViewer');
          topicRecord.setValue((currentFollowerCount ?? 0) + 1, 'followerCount');
        }
      },
      onError: (error) => {
        console.error('Failed to follow topic:', error);
      },
    });
  }, [commitFollow, allTopics]);

  const handleUnfollow = useCallback((topicSlug: string) => {
    const topicId = allTopics.find((t) => t.slug === topicSlug)?.id;
    if (!topicId) return;
    commitUnfollow({
      variables: {
        input: { topicSlug },
      },
      optimisticUpdater: (store) => {
        const topicRecord = store.get(topicId);
        if (topicRecord) {
          const currentFollowerCount = topicRecord.getValue('followerCount') as number | null;
          topicRecord.setValue(false, 'isFollowedByViewer');
          topicRecord.setValue(Math.max(0, (currentFollowerCount ?? 0) - 1), 'followerCount');
        }
      },
      onError: (error) => {
        console.error('Failed to unfollow topic:', error);
      },
    });
  }, [commitUnfollow, allTopics]);

  const isPending = isFollowingMutation || isUnfollowingMutation;

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Discover</h2>
        <p className="mt-1 text-gray-400">
          Find topics to follow and stay updated with new episodes
        </p>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <svg
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-700 bg-gray-800/50 py-2.5 pl-10 pr-4 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {filteredTopics.length === 0 ? (
        <EmptyState searchQuery={searchQuery} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredTopics.map((topic) => (
              <TopicCard
                key={topic.id}
                topic={topic}
                onFollow={handleFollow}
                onUnfollow={handleUnfollow}
                isPending={isPending}
              />
            ))}
          </div>

          {hasNext && !searchQuery && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => loadNext(50)}
                disabled={isLoadingNext}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingNext ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function DiscoverPageLoader() {
  const data = useLazyLoadQuery<DiscoverPageQuery>(discoverPageQuery, {}, {
    fetchPolicy: 'store-and-network',
  });

  return <DiscoverPageContent queryRef={data} />;
}

export function DiscoverPage() {
  return (
    <QueryErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        }
      >
        <DiscoverPageLoader />
      </Suspense>
    </QueryErrorBoundary>
  );
}
